/**
 * Who can be named on a share link — the dashboard's one directory for every
 * share surface (`SharePeopleProvider`, `@authe/saved-views`).
 *
 * The share dialog used to take `teamOptions` per call site and no people at
 * all, so "Specific people" took raw identity-provider ids and the "who opened"
 * panel named nobody. Mounted once beside `SavedViewsProvider`:
 *
 * - **people**: the organization's ACTIVE members, from the Team page's own
 *   cached read (`useOrgMembers`, permissions `users:list` — read tier).
 *   Pending invitations are left out: they have no identity to open a link
 *   with, and the server refuses them.
 * - **teams**: the caller's memberships (`useShareTeamOptions`), with the
 *   failed-read state and its retry — the dialog says "couldn't be loaded"
 *   beside the picker, for every caller, instead of each page toasting it.
 * - **me**: from the session, so the caller can always pick themselves even
 *   when the member read failed.
 *
 * Lazy: nothing is read until a share surface asks (`request`), so opening the
 * dashboard does not list the organization just in case a dialog opens.
 */
import * as React from "react";
import { SharePeopleProvider, type SharePeople } from "@authe/saved-views";
import { useSession } from "@authe/ui-core/session";
import { useOrgMembers } from "@/features/team/useOrgMembers";
import { useShareTeamOptions } from "./savedViewPermissions";

export function DashboardSharePeople({ children }: { children: React.ReactNode }): React.ReactElement {
  const [wanted, setWanted] = React.useState(false);
  const request = React.useCallback(() => setWanted(true), []);
  const { members, isLoading, error } = useOrgMembers({ enabled: wanted });
  const { options: teams, failed: teamsFailed, refetch: retryTeams } = useShareTeamOptions();
  const { session } = useSession();
  const me = session?.user;

  const value = React.useMemo<SharePeople>(
    () => ({
      users: members
        .filter((m) => !m.pending && m.status === "active" && m.id && !m.id.includes("@"))
        .map((m) => ({ id: m.id, label: m.name || m.email || m.id, ...(m.email ? { detail: m.email } : {}) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      teams,
      currentUser: me?.id ? { id: me.id, label: me.name || me.email || "Me", ...(me.email ? { detail: me.email } : {}) } : null,
      status: !wanted || isLoading ? "loading" : error ? "unavailable" : "ready",
      teamsStatus: teamsFailed ? "unavailable" : "ready",
      retryTeams,
      request,
    }),
    [members, teams, teamsFailed, retryTeams, me?.id, me?.name, me?.email, wanted, isLoading, error, request],
  );
  return <SharePeopleProvider value={value}>{children}</SharePeopleProvider>;
}
